import React, {useCallback, useEffect, useState} from 'react';
import {AlertTriangleIcon, CheckCheckIcon} from '../components/icons';
import AuthShell from '../components/auth/AuthShell';
import SmartCaptcha, {isCaptchaEnabled} from '../components/auth/SmartCaptcha';
import Spinner from '../components/Spinner';
import {AuthApiError, resendVerification, verifyEmail} from '../service/RegistrationService';
import {useDocumentTitle} from '../hooks/useDocumentTitle';

type VerifyState = 'loading' | 'success' | 'error';

/**
 * Подтверждение адреса по ссылке из письма
 * (`docs/features/2026-08-20_auth-registration-recovery`, FR-24).
 *
 * Токен приходит в query и сразу отправляется на сервер. Ошибка не различает
 * причины (просрочен / уже использован / неизвестен) — как и сервер (SEC-3),
 * поэтому единственный полезный выход — запросить письмо заново.
 */
const VerifyEmailPage: React.FC = () => {
    useDocumentTitle();
    const [state, setState] = useState<VerifyState>('loading');
    const [message, setMessage] = useState('');
    const [email, setEmail] = useState('');
    const [captchaToken, setCaptchaToken] = useState('');
    const [resent, setResent] = useState(false);
    const [resending, setResending] = useState(false);

    const captchaRequired = isCaptchaEnabled();
    const handleCaptchaToken = useCallback((token: string) => setCaptchaToken(token), []);

    useEffect(() => {
        const token = new URLSearchParams(window.location.search).get('token') || '';
        if (!token) {
            setState('error');
            setMessage('Ссылка неполная: в ней нет кода подтверждения.');
            return;
        }
        let cancelled = false;
        verifyEmail(token)
            .then(() => {
                if (!cancelled) {
                    setState('success');
                }
            })
            .catch((err) => {
                if (cancelled) {
                    return;
                }
                setState('error');
                setMessage(err instanceof AuthApiError
                    ? err.description
                    : 'Не удалось подтвердить адрес. Попробуйте позже.');
            });
        return () => {
            cancelled = true;
        };
    }, []);

    const onResend = async (e: React.FormEvent) => {
        e.preventDefault();
        setResending(true);
        try {
            await resendVerification(email, captchaToken);
            setResent(true);
        } catch (err) {
            setMessage(err instanceof AuthApiError ? err.description : 'Не удалось отправить письмо.');
        } finally {
            setResending(false);
        }
    };

    return (
        <AuthShell
            heading={<>Подтверждение<br/><span className="text-brand-600 dark:text-brand-400">адреса почты</span></>}
            lead="Подтверждение нужно один раз: так мы убеждаемся, что адрес принадлежит вам, и сможем восстановить доступ, если вы забудете пароль."
        >
            <div data-testid="verify-email-page" className="mt-6 text-center">
                {state === 'loading' && (
                    <>
                        <Spinner/>
                        <p className="mt-4 text-sm text-ink-500 dark:text-ink-400">Подтверждаем адрес…</p>
                    </>
                )}

                {state === 'success' && (
                    <div data-testid="verify-success">
                        <span className="mx-auto w-12 h-12 rounded-full bg-brand-50 dark:bg-brand-500/10 flex items-center justify-center">
                            <CheckCheckIcon className="w-6 h-6 text-brand-600 dark:text-brand-400"/>
                        </span>
                        <h2 className="mt-4 text-2xl font-bold text-ink-900 dark:text-ink-50">Адрес подтверждён</h2>
                        <p className="mt-3 text-sm leading-6 text-ink-500 dark:text-ink-400">
                            Аккаунт активирован — можно входить и собирать гардероб.
                        </p>
                        <a href="/login" className="btn-accent h-12 mt-6 w-full">Войти</a>
                    </div>
                )}

                {state === 'error' && (
                    <div data-testid="verify-error">
                        <span className="mx-auto w-12 h-12 rounded-full bg-amber-50 dark:bg-amber-500/10 flex items-center justify-center">
                            <AlertTriangleIcon className="w-6 h-6 text-amber-600 dark:text-amber-400"/>
                        </span>
                        <h2 className="mt-4 text-2xl font-bold text-ink-900 dark:text-ink-50">Ссылка не подошла</h2>
                        <p className="mt-3 text-sm leading-6 text-ink-500 dark:text-ink-400" role="alert">{message}</p>

                        {resent ? (
                            <p className="mt-6 text-sm text-ink-600 dark:text-ink-300" data-testid="resend-done">
                                Если такой аккаунт существует и ещё не подтверждён, мы отправили новое письмо.
                            </p>
                        ) : (
                            <form onSubmit={onResend} className="mt-6 space-y-4 text-left">
                                <div>
                                    <label htmlFor="resend-email"
                                           className="block text-sm font-medium text-ink-700 dark:text-ink-200 mb-2">
                                        Электронная почта
                                    </label>
                                    <input
                                        id="resend-email"
                                        type="email"
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        className="input-field h-12"
                                        placeholder="you@example.com"
                                        autoComplete="email"
                                        required
                                    />
                                </div>
                                <SmartCaptcha onToken={handleCaptchaToken}/>
                                <button
                                    type="submit"
                                    disabled={resending || (captchaRequired && !captchaToken)}
                                    className="btn-accent w-full h-12 text-[15px]"
                                >
                                    {resending ? 'Отправляем...' : 'Отправить письмо заново'}
                                </button>
                            </form>
                        )}

                        <a href="/login" className="btn-secondary h-11 mt-4 w-full">Вернуться ко входу</a>
                    </div>
                )}
            </div>
        </AuthShell>
    );
};

export default VerifyEmailPage;
